/* =============================================================================
 * 12th Tribe — GA4 engagement dataLayer
 * Gamma Waves / Sellry  ·  Theme: Shopify Horizon  ·  GTM: GTM-K58PBVK
 *
 * WHAT THIS IS
 *   Pushes the dataLayer events that GTM turns into GA4 engagement events for
 *   the site's merchandising and navigation surfaces. Loaded with `defer` from
 *   snippets/datalayer-events.liquid (rendered in layout/theme.liquid).
 *   Every push is guarded, so a tracking error never breaks the storefront.
 *
 * CONTRACT (dataLayer event -> GA4 event; GTM trigger is "CE - <event>")
 *   PART 1  Mega-menu        navigation_click      -> navigation_click
 *   PART 2  Shop the Look    stl_view / stl_scroll / stl_item_click
 *                            stl_add_to_cart       -> add_to_cart
 *   APPENDED carousels       ymal_*    You May Also Like  (PDP related recs)
 *                            rv_*      Recently Viewed
 *                            cartctl_* Cart drawer carousels: Free Shipping,
 *                                      Complete the Look, Trending Now
 *                                      (told apart by placement)
 *                            each: _view / _scroll / _item_click, and
 *                            _add_to_cart          -> add_to_cart
 *   APPENDED PLP grids       select_item           -> select_item
 *
 *   Surface adds push a surface-specific event name (stl_add_to_cart, ...) so
 *   they don't collide with the site-wide add_to_cart trigger. The GTM tags
 *   send GA4 a standard `add_to_cart` with ecommerce.items[].item_list_id /
 *   item_list_name and the surface's *_source param. Every event above carries
 *   `placement` and `placement_type` (e.g. pdp_shop_the_look / carousel).
 *
 * NOT IN THIS FILE: the Complete the Look and Pre-Order popup tracking.
 *   snippets/pdp-popup.liquid pushes cstm_completelook_* / cstm_altstyles_*,
 *   which reach GA4 as completelook_* / altstyles_*. It also sets the pending
 *   surface add for popup adds (_list_source "Complete The Look" /
 *   "Alternative Styles Pop-up").
 *
 * window.TT API (used by other theme files)
 *   getPendingSurfaceAdd / setPendingSurfaceAdd
 *     Pending surface add. assets/product-form.js reads it to write the
 *     `_list_source` line-item property; the Shopify custom pixel copies it to
 *     item_list_name on its checkout_completed dataLayer event.
 * ========================================================================== */
(function () {
  'use strict';
  window.dataLayer = window.dataLayer || [];
  var dl = window.dataLayer;
  function push(obj) { try { dl.push(obj); } catch (e) { /* never break the storefront */ } }
  function clean(s) { return (s == null ? '' : String(s)).replace(/\s+/g, ' ').trim(); }
  function money(v) { var n = parseFloat(v); return isNaN(n) ? undefined : n; }
  function getData(el, name) {
    return el && el.dataset ? el.dataset[name] || '' : '';
  }
  function closestData(el, name) {
    var node = el;
    while (node && node !== document) {
      if (node.dataset && node.dataset[name]) return node.dataset[name];
      node = node.parentElement;
    }
    return '';
  }
  function cardData(el) {
    var card = el && el.closest('[data-product-id], [data-variant-id], product-card, .resource-card, .resource-list__item');
    return {
      id: getData(card, 'variantId') || getData(card, 'productId'),
      productId: getData(card, 'productId'),
      name: getData(card, 'productTitle') || clean(card && card.querySelector('[class*="title"], a')?.textContent),
      price: getData(card, 'productPrice') || (card && card.querySelector('[class*="price"]')?.textContent || '').replace(/[^0-9.]/g, ''),
      quantity: 1,
      slideIndex: getData(card, 'slideIndex')
    };
  }
  function setPendingSurfaceAdd(surface, item) {
    window.__ttPendingSurfaceAdd = {
      surface: surface,
      item: item || {},
      createdAt: Date.now()
    };
  }
  function getPendingSurfaceAdd() {
    var pending = window.__ttPendingSurfaceAdd;
    if (!pending || !pending.createdAt || Date.now() - pending.createdAt > 30 * 60 * 1000) {
      window.__ttPendingSurfaceAdd = null;
      return null;
    }
    return pending;
  }
  window.TT = window.TT || {};
  window.TT.getPendingSurfaceAdd = getPendingSurfaceAdd;
  // Exposed so other surfaces (e.g. snippets/pdp-popup.liquid) can flag a
  // pending surface add, which assets/product-form.js reads to write the
  // _list_source line-item property for purchase attribution.
  window.TT.setPendingSurfaceAdd = setPendingSurfaceAdd;
  function linkPosition(link, header) {
    var links = Array.prototype.slice.call(header.querySelectorAll('[data-nav-level], a.meganav__link, a.meganav__card, a.menu-list__link'));
    var index = links.indexOf(link);
    return index > -1 ? index + 1 : undefined;
  }
  function markStlEngaged() {
    try {
      sessionStorage.setItem('tt_stl_engaged', 'true');
    } catch (e) {}
    return true;
  }

  /* ===========================================================================
   * PART 1 — HEADER / MEGA-MENU NAVIGATION
   * One delegated listener on <header-component>. Reads the menu hierarchy from
   * data-nav-level (top | column | item | card), data-nav-top and
   * data-nav-column, which blocks/_main-menu.liquid and
   * blocks/_meganav-column.liquid stamp on each link. For links without them,
   * nav_level falls back to the link's class.
   * ========================================================================= */
  (function navigation() {
    var header = document.querySelector('header-component') ||
                 document.querySelector('.header') ||
                 document.querySelector('header');
    if (!header) return;

    function levelFromClass(a) {
      var c = a.className || '';
      if (c.indexOf('menu-list__link') > -1) return 'top';
      if (c.indexOf('meganav__card') > -1) return 'card';
      if (c.indexOf('meganav__heading') > -1) return 'column';
      if (c.indexOf('meganav__link') > -1) return 'item';
      return 'item';
    }

    header.addEventListener('click', function (e) {
      var a = e.target.closest(
        '[data-nav-level], a.meganav__link, a.meganav__card, a.menu-list__link'
      );
      if (!a || !header.contains(a)) return;

      var topLevel = clean(a.dataset.navTop || closestData(a, 'navTop'));
      var column = clean(a.dataset.navColumn || closestData(a, 'navColumn'));

      push({
        event: 'navigation_click',
        placement: 'header_navigation',
        placement_type: 'nav',
        nav_level: a.dataset.navLevel || levelFromClass(a),
        nav_top_level: topLevel,
        nav_column: column,
        link_text: clean(a.textContent),          // fixes the "\nDRESSES\n" padding
        link_url: a.href || a.getAttribute('href') || '',
        link_position: linkPosition(a, header)
      });
    }, true); // capture phase: fires before SPA navigation tears the link out
  })();

  /* ===========================================================================
   * SHARED HELPER — emit a surface add-to-cart.
   *   surface: { event, listId, listName, sourceKey, sourceVal,
   *              placement, placementType }
   *   item:    { id (sku/variant), name, price, quantity }
   *
   * IMPORTANT (contract): the dataLayer `event` is the SURFACE-SPECIFIC name
   * (stl_add_to_cart / ymal_add_to_cart / rv_add_to_cart / cartctl_add_to_cart)
   * so the surface's GTM Custom Event trigger fires and we DON'T collide with
   * the site-wide add_to_cart trigger. The GTM tags themselves are configured with
   * eventName = "add_to_cart", so GA4 still receives a standard add_to_cart
   * with item_list attribution. Do not change the event name to "add_to_cart"
   * here or the new tags will not fire.
   * ========================================================================= */
  function pushSurfaceAddToCart(surface, item) {
    var obj = {
      event: surface.event,
      placement: surface.placement,
      placement_type: surface.placementType,
      ecommerce: {
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
        value: money(item.price),
        items: [{
          item_id: item.id,
          item_name: clean(item.name),
          price: money(item.price),
          quantity: item.quantity || 1,
          item_list_id: surface.listId,
          item_list_name: surface.listName
        }]
      }
    };
    obj[surface.sourceKey] = surface.sourceVal;      // stl_source / ymal_source / rv_source / cartctl_source
    push({ ecommerce: null });                       // clear previous ecommerce object (GA4 best practice)
    push(obj);
  }

  // Confirm a carousel surface add (Shop the Look, YMAL, Recently Viewed, and
  // cart drawer quick-add-dialog adds) on the theme's cart:update.
  //
  // INTENT-BASED — do NOT gate on event.detail.data.listSource. The theme's
  // CartAddEvent does not set listSource, and carousel quick-adds go through
  // GlobalQuickBuy (assets/quick-add.js), which dispatches cart:update with NO
  // `extra` and NO listSource at all, so such a guard would always bail.
  //
  // Instead: the quick-add click records a pending add via setPendingSurfaceAdd
  // (with child data captured from the card). Confirm it on the next
  // cart:update regardless of payload, preferring pending.item (card data) and
  // enriching from `extra` when present.
  document.addEventListener('cart:update', function (event) {
    var pending = getPendingSurfaceAdd();
    if (!pending) return;
    // Only fire for the carousel surfaces in this allowlist. The popups
    // (pdp-popup.liquid) also set a pending add (for _list_source) but fire
    // their own add events, so skip those here.
    var STL_ADD_EVENTS = ['stl_add_to_cart','cartctl_add_to_cart','ymal_add_to_cart','rv_add_to_cart'];
    if (pending.surface && STL_ADD_EVENTS.indexOf(pending.surface.event) === -1) return;
    var data = (event.detail && event.detail.data) || {};
    if (data.didError) return;

    var extra = data.extra || {};
    var id = extra.item_id || pending.item.id || data.variantId || '';
    var name = extra.item_name || pending.item.name || '';
    // require we identified the child product (covers empty cart:update payloads)
    if (!id && !name) return;

    pushSurfaceAddToCart(pending.surface, {
      id: id,
      name: name,
      // extra.price (Flow A) and pending.item.price (card data-product-price) are
      // already dollar strings; never divide again.
      price: extra.price || pending.item.price || '',
      quantity: extra.quantity || pending.item.quantity || 1
    });
    window.__ttPendingSurfaceAdd = null;
  });

  /* ===========================================================================
   * PART 2 — SHOP THE LOOK (PDP native <product-recommendations> carousel)
   * Hooks:
   *   container:  product-recommendations[data-intent="complementary"]
   *   carousel:   slideshow-component  (controls: .slideshow-control--next/--previous)
   *   item link:  a[href*="/products/"]
   *   quick add:  <quick-add-component data-quick-add-button>  (see (B) below)
   * ========================================================================= */
  (function shopTheLook() {
    var stl = document.querySelector('product-recommendations[data-intent="complementary"]');
    if (!stl) return;
    var parentId = stl.dataset.productId || '';
    var parentName = clean(stl.dataset.parentProductName || document.querySelector('.product-title, h1')?.textContent);

    // (A) view — once, when ≥50% visible
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            push({
              event: 'stl_view',
              placement: 'pdp_shop_the_look',
              placement_type: 'carousel',
              parent_product_id: parentId,
              parent_product_name: parentName,
              stl_engaged: markStlEngaged(),
              item_count: stl.querySelectorAll('a[href*="/products/"]').length
            });
            obs.disconnect();
          }
        });
      }, { threshold: 0.5 });
      io.observe(stl);
    }

    // delegated: scroll controls + item clicks
    stl.addEventListener('click', function (e) {
      var ctrl = e.target.closest('.slideshow-control');
      if (ctrl) {
        push({
          event: 'stl_scroll',
          placement: 'pdp_shop_the_look',
          placement_type: 'carousel',
          parent_product_id: parentId,
          stl_engaged: markStlEngaged(),
          direction: ctrl.classList.contains('slideshow-control--next') ? 'next' : 'prev',
          slide_index: currentSlideIndex(stl)
        });
        return;
      }
      var link = e.target.closest('a[href*="/products/"]');
      if (link) {
        var card = cardData(link);
        push({
          event: 'stl_item_click',
          placement: 'pdp_shop_the_look',
          placement_type: 'carousel',
          parent_product_id: parentId,
          stl_engaged: markStlEngaged(),
          child_product_id: card.productId || '',
          child_product_name: card.name || clean(link.textContent),
          slide_index: card.slideIndex || currentSlideIndex(stl)
        });
      }
    });

    // (B) add — set pending intent when the carousel quick-add is clicked.
    // The quick-add control is <quick-add-component data-quick-add-button="choose">,
    // the same hook the theme uses; button[name="add"] / .quick-add__button are
    // kept only as fallbacks. Product data lives on the ancestor <product-card> /
    // .resource-list__item (data-product-id / data-variant-id /
    // data-product-title / data-product-price), which cardData() reads.
    stl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add-button], button[name="add"], .quick-add__button');
      if (!btn || !stl.contains(btn)) return;
      setPendingSurfaceAdd(
        { event: 'stl_add_to_cart', listId: 'shop_the_look', listName: 'Shop the Look', sourceKey: 'stl_source', sourceVal: parentId,
          placement: 'pdp_shop_the_look', placementType: 'carousel' },
        cardData(btn)
      );
    });

    function currentSlideIndex(scope) {
      var sc = scope.querySelector('slideshow-component');
      return sc && sc.getAttribute('current-slide') ? Number(sc.getAttribute('current-slide')) : undefined;
    }
  })();

  /* ===========================================================================
   * PURCHASE ATTRIBUTION (carry the surface through to purchase)
   * The Shopify custom pixel can't read theme JS, so a line-item property is the
   * bridge. When a surface add is pending, assets/product-form.js writes the
   * pending surface's listName as properties[_list_source] (e.g. 'Shop the
   * Look'). At checkout_completed the custom pixel copies _list_source into
   * items[].item_list_name on its dataLayer event.
   *
   * GA4's purchase event doesn't carry it yet: no GTM tag sends purchase from
   * that event, so per-surface revenue isn't in GA4.
   * ========================================================================= */

  /* ===== APPENDED: additional carousels + select_item ===== */

/* ---------------------------------------------------------------------------
 * Generic carousel wiring — used by YMAL, Recently Viewed and the cart drawer.
 * root: the container element.
 * surface: { prefix, listId, listName, sourceKey, placement, placementType }.
 * getParentId: function -> the "source" product id for attribution.
 * ------------------------------------------------------------------------- */
function ttWireCarousel(root, surface, getParentId) {
  if (!root || root.__ttCarouselBound) return;
  root.__ttCarouselBound = true;
  var parentId = (getParentId && getParentId()) || '';

  // view — once when >=50% visible (root may be async-injected; observe now)
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          push({
            event: surface.prefix + '_view',
            placement: surface.placement,
            placement_type: surface.placementType,
            parent_product_id: parentId,
            item_count: root.querySelectorAll('a[href*="/products/"]').length
          });
          obs.disconnect();
        }
      });
    }, { threshold: 0.5 });
    io.observe(root);
  }

  // delegated: scroll arrows, item clicks, quick-add (intent)
  root.addEventListener('click', function (e) {
    var ctrl = e.target.closest('.slideshow-control, slideshow-arrows button');
    if (ctrl) {
      push({
        event: surface.prefix + '_scroll',
        placement: surface.placement,
        placement_type: surface.placementType,
        parent_product_id: parentId,
        direction: (ctrl.classList.contains('slideshow-control--next') || ctrl.getAttribute('ref') === 'next') ? 'next' : 'prev'
      });
      return;
    }
    var qa = e.target.closest('[data-quick-add-button], button[name="add"], .quick-add__button');
    if (qa && root.contains(qa)) {
      setPendingSurfaceAdd(
        { event: surface.prefix + '_add_to_cart', listId: surface.listId, listName: surface.listName, sourceKey: surface.sourceKey, sourceVal: parentId,
          placement: surface.placement, placementType: surface.placementType },
        cardData(qa)
      );
      return;
    }
    var link = e.target.closest('a[href*="/products/"]');
    if (link && root.contains(link)) {
      var card = cardData(link);
      push({
        event: surface.prefix + '_item_click',
        placement: surface.placement,
        placement_type: surface.placementType,
        parent_product_id: parentId,
        child_product_id: card.productId || '',
        child_product_name: card.name || clean(link.textContent)
      });
    }
  });
}

/* ---------------------------------------------------------------------------
 * 1. PDP "You May Also Like" — native related product-recommendations.
 *    Same element type as STL, but intent="related". Present at/after load.
 * ------------------------------------------------------------------------- */
(function ymal() {
  function init() {
    var el = document.querySelector('product-recommendations[data-intent="related"]');
    if (!el) return false;
    ttWireCarousel(el, { prefix: 'ymal', listId: 'you_may_also_like', listName: 'You May Also Like', sourceKey: 'ymal_source',
      placement: 'pdp_you_may_also_like', placementType: 'carousel' },
      function () { return el.dataset.productId || ''; });
    return true;
  }
  if (!init() && 'MutationObserver' in window) {
    var mo = new MutationObserver(function () { if (init()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();

/* ---------------------------------------------------------------------------
 * 2. PDP "Recently Viewed" — <recently-viewed-products>, JS-rendered async via
 *    Search API. Cards (.resource-list__item / product-card) appear after fetch.
 * ------------------------------------------------------------------------- */
(function recentlyViewed() {
  function init() {
    var el = document.querySelector('recently-viewed-products');
    // wait until it actually has product cards (async render)
    if (!el || !el.querySelector('a[href*="/products/"]')) return false;
    ttWireCarousel(el, { prefix: 'rv', listId: 'recently_viewed', listName: 'Recently Viewed', sourceKey: 'rv_source',
      placement: 'pdp_recently_viewed', placementType: 'carousel' },
      function () { return (document.querySelector('[data-product-id]') || {}).dataset?.productId || ''; });
    return true;
  }
  if (!init() && 'MutationObserver' in window) {
    var mo = new MutationObserver(function () { if (init()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();

/* ---------------------------------------------------------------------------
 * 3. Cart drawer carousels — snippets/cart-drawer-carousel.liquid, used by the
 *    recommendations carousel and the empty bag's Trending now.
 *
 *    Each carousel's container carries its GA4 list on data attributes:
 *      data-tt-list-id / data-tt-list-name / data-tt-placement
 *    The recommendations carousel switches list when the cart crosses the free
 *    shipping threshold without being replaced (the section morphs), so the list is
 *    read from the container at event time, not bound once:
 *      below the threshold  "Cart Drawer Free Shipping"     cart_drawer_free_shipping
 *      reached / not eligible "Cart Drawer Complete the Look" cart_drawer_complete_the_look
 *      empty bag            "Cart Drawer Trending Now"      cart_drawer_trending_now
 *    All three use the cartctl_* events (view / scroll / item_click / add_to_cart),
 *    so the existing GTM tags carry them; placement tells them apart.
 *
 *    Quick add opens the shared quick-add modal (GlobalQuickBuy in
 *    assets/quick-add.js), so the add is confirmed by the shared pending-add
 *    listener above on the next cart:update. GlobalQuickBuy also writes
 *    _list_source from the pending add for these lists.
 * ------------------------------------------------------------------------- */
(function cartDrawerCarousels() {
  var ROOT_SELECTOR = '#cart-drawer [data-tt-list-id]';

  function surfaceFor(root) {
    return {
      listId: root.dataset.ttListId,
      listName: root.dataset.ttListName,
      placement: root.dataset.ttPlacement,
      placementType: 'carousel'
    };
  }

  // view — once per list per carousel element, when at least half of it is visible
  var observer = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var root = entry.target;
      var surface = surfaceFor(root);
      if (root.__ttViewedList === surface.listId) return;
      root.__ttViewedList = surface.listId;
      push({
        event: 'cartctl_view',
        placement: surface.placement,
        placement_type: surface.placementType,
        parent_product_id: '',
        item_count: root.querySelectorAll('[data-product-id]').length
      });
    });
  }, { threshold: 0.5 }) : null;

  function observeCarousels() {
    if (!observer) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      // A carousel that switched list may be viewed again.
      if (root.__ttObservedList === root.dataset.ttListId) return;
      root.__ttObservedList = root.dataset.ttListId;
      observer.unobserve(root);
      observer.observe(root);
    });
  }

  document.addEventListener('click', function (e) {
    var root = e.target.closest && e.target.closest(ROOT_SELECTOR);
    if (!root) return;
    var surface = surfaceFor(root);

    var arrow = e.target.closest('[data-carousel-direction]');
    if (arrow) {
      if (arrow.getAttribute('aria-disabled') === 'true') return;
      push({
        event: 'cartctl_scroll',
        placement: surface.placement,
        placement_type: surface.placementType,
        parent_product_id: '',
        direction: arrow.dataset.carouselDirection === 'next' ? 'next' : 'prev'
      });
      return;
    }

    var quickAdd = e.target.closest('[data-quick-add-button]');
    if (quickAdd) {
      setPendingSurfaceAdd(
        { event: 'cartctl_add_to_cart', listId: surface.listId, listName: surface.listName, sourceKey: 'cartctl_source', sourceVal: '',
          placement: surface.placement, placementType: surface.placementType },
        cardData(quickAdd)
      );
      return;
    }

    var link = e.target.closest('a[href*="/products/"]');
    if (link) {
      var card = cardData(link);
      push({
        event: 'cartctl_item_click',
        placement: surface.placement,
        placement_type: surface.placementType,
        parent_product_id: '',
        child_product_id: card.productId || '',
        child_product_name: card.name || clean(link.textContent)
      });
    }
  });

  // A pending drawer add whose quick-add modal closed without adding to cart must not be
  // attributed to a later, unrelated quick add. Successful adds are confirmed (and cleared)
  // on cart:update before the modal finishes closing.
  function bindQuickAddDialog() {
    var dialog = document.getElementById('quick-add-dialog');
    if (!dialog || dialog.__ttDrawerPendingBound) return;
    dialog.__ttDrawerPendingBound = true;
    dialog.addEventListener('dialog:close', function () {
      var pending = getPendingSurfaceAdd();
      if (pending && pending.surface && String(pending.surface.listName || '').indexOf('Cart Drawer') === 0) {
        window.__ttPendingSurfaceAdd = null;
      }
    });
  }

  observeCarousels();
  bindQuickAddDialog();
  document.addEventListener('cart:open', observeCarousels);
  document.addEventListener('cart:update', function () { setTimeout(observeCarousels, 300); });
  if ('MutationObserver' in window) {
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        observeCarousels();
        bindQuickAddDialog();
      });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tt-list-id'] });
  }
})();

/* ---------------------------------------------------------------------------
 * 4. select_item — collection / PLP list item clicks.
 *    GA4 standard event; pairs with Fueled's view_item_list (see NOTE below).
 *    Fires when a product card link is clicked within a collection/PLP resource
 *    list (NOT inside the tracked surface carousels above, which have their own
 *    *_item_click). item_list_name comes from the collection page heading.
 * ------------------------------------------------------------------------- */
(function selectItem() {
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href*="/products/"]');
    if (!link) return;
    // skip surfaces we already track with their own *_item_click
    if (link.closest('product-recommendations, recently-viewed-products, #cart-drawer, #pdp-popup, .cart-recommendations, [data-morph-ignore]')) return;
    // PLP structure (snippets/product-grid.liquid): ul.product-grid[data-testid="product-grid"] > li.product-grid__item > product-card
    var grid = link.closest('.product-grid, [data-testid="product-grid"], .resource-list');
    if (!grid) return;
    var card = link.closest('product-card') || link.closest('.product-grid__item') || link;
    // list name = collection <h1> (matches Fueled's item_list_name convention, e.g. "Dresses")
    var listName = clean(document.querySelector('.collection-hero__title, h1')?.textContent || document.title);
    push({ ecommerce: null });
    push({
      event: 'select_item',
      placement: 'collection_grid',
      placement_type: 'grid',
      ecommerce: {
        item_list_name: listName,
        items: [{
          item_id: (card.dataset && (card.dataset.variantId || card.dataset.productId)) || '',
          item_name: (card.dataset && card.dataset.productTitle) || clean(link.textContent),
          price: (card.dataset && card.dataset.productPrice) || undefined,
          item_list_name: listName
        }]
      }
    });
  });
})();

/* ---------------------------------------------------------------------------
 * NOTE on select_item consistency (read before relying on it):
 * The existing view_item_list is generated by FUELED server-side, NOT
 * the page dataLayer. This client-side select_item is a separate mechanism, so the
 * view→select funnel only pairs cleanly if item_list_name values match. We derive
 * the list name from the collection <h1> ("Dresses", "New Arrivals", ...), which
 * matches the collection-title convention Fueled's view_item_list uses — so they
 * SHOULD align for standard collection pages. For guaranteed consistency (and to
 * cover search/other lists), the cleaner long-term option is to have Fueled emit
 * select_item alongside its view_item_list. Confirm list-name alignment in GA4
 * before treating the funnel as authoritative.
 * ------------------------------------------------------------------------- */

})();